import React from 'react';
import { Box, Typography } from '@mui/material';
import { brandHome } from '../../brandHomeTokens';
import { StoreIssuesPanel } from './StoreIssuesPanel';
import { StoreSetupPanel } from './StoreSetupPanel';
import type { StoreViewModel } from '../../storeViewModel';
import { storeInitials } from '../../storeViewModel';

interface FewStoreLayoutProps {
  stores: StoreViewModel[];
}

export const FewStoreLayout: React.FC<FewStoreLayoutProps> = ({ stores }) => (
  <>
    {stores.map((store) => {
      const total = store.includedProductCount + store.excludedProductCount;
      const includedPct = total > 0 ? (store.includedProductCount / total) * 100 : 0;
      const excludedPct = total > 0 ? 100 - includedPct : 0;

      return (
        <Box key={store.storeId} sx={{ p: '16px 20px', borderBottom: `1px solid ${brandHome.color.rowHairline}`, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: `${brandHome.radius.control}px`,
                bgcolor: brandHome.color.infoTint,
                color: brandHome.color.infoInk,
                display: 'grid',
                placeItems: 'center',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {storeInitials(store.storeName)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
              <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{store.storeName}</Typography>
              <Typography sx={{ fontSize: '12px', color: brandHome.color.muted }}>
                {store.setupIncomplete ? 'Setting up' : store.platforms.join(', ') || 'Unknown platform'}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>
                {store.setupIncomplete
                  ? store.integrationConnected
                    ? store.platforms.join(', ') || 'Connected'
                    : 'Not connected'
                  : store.lastSyncAt
                    ? `Last sync ${new Date(store.lastSyncAt).toLocaleDateString()}`
                    : 'Never synced'}
              </Typography>
            </Box>
          </Box>
          {store.setupIncomplete ? (
            <StoreSetupPanel steps={store.setupSteps} />
          ) : (
            <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: brandHome.color.muted }}>
                  <span>{store.includedProductCount.toLocaleString()} included{store.countsArePartial ? '+' : ''}</span>
                  <span>{store.excludedProductCount.toLocaleString()} excluded</span>
                </Box>
                <Box sx={{ display: 'flex', height: 8, borderRadius: '5px', overflow: 'hidden', bgcolor: brandHome.color.hairline }}>
                  <Box sx={{ width: `${includedPct}%`, bgcolor: brandHome.color.positiveSolid }} />
                  <Box sx={{ width: `${excludedPct}%`, bgcolor: brandHome.color.cautionSolid }} />
                </Box>
              </Box>
            </Box>
          )}
          {/* Same copy as the setup panel's Authorization step, so a syncing
              store with a self-declared dealer claim does not read as rejected. */}
          {!store.setupIncomplete && store.authorizationNote && (
            <Typography sx={{ fontSize: '12.5px', color: brandHome.color.muted }}>
              Authorization: {store.authorizationNote}
            </Typography>
          )}
          {/* MAP can exist without finished setup; keep issues visible beside the setup panel. */}
          <StoreIssuesPanel issues={store.issues} />
        </Box>
      );
    })}
  </>
);
